import { useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Reader, readerStatus } from '../../models/Reader'
import { RecordFactory } from '../../models/RecordFactory'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import { ListHeader } from '../common/ListHeader'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Select } from '../common/fields/Select'
import { SetupReaderPage } from './SetupReaderPage'

const viewWillAppear = async () => {
    const readers = await RecordFactory.getInstance().loadReaders()
    return { readers }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = usePSNavigate()
    const readers = data.readers
    const [readerToDelete, setReaderToDelete] = useState<Reader | null>(null)
    const [isProcessing, setIsProcessing] = useState(false)

    const editReader = (reader: Reader) => {
        navigate(`/settings/reader/${reader.id}/edit`, { state: { reader } })
    }

    const registerReader = (reader: Reader) => {
        navigate(`/settings/reader/${reader.id}/activate`, { state: { reader } })
    }

    const handleConfirmDelete = async () => {
        if (readerToDelete?.id) {
            setIsProcessing(true)
            await RecordFactory.getInstance().deleteReader(readerToDelete.id)
            setIsProcessing(false)
        }
        setReaderToDelete(null)
        base.reload()
    }

    if (readers.length === 0) {
        return <SetupReaderPage />
    }

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-6">
            <ListHeader
                title={
                    <>
                        Card readers{' '}
                        <span className="text-base leading-5 font-bold text-gray-600">
                            ({readers.length})
                        </span>
                    </>
                }
                backButton={{
                    label: 'Settings',
                    href: '/settings',
                }}
                actionButton={{
                    icon: 'plus.small',
                    intent: 'primary',
                    buttonLabel: 'Add reader',
                    actions: [
                        {
                            id: 'new',
                            label: 'Order a new reader',
                            icon: 'shoppingCart',
                            href: '/settings/reader/checkout',
                        },
                        {
                            id: 'existing',
                            label: 'Add an existing reader',
                            icon: 'plus',
                            href: '/settings/reader/add',
                        },
                    ],
                }}
            />

            <div className="mt-6 rounded-lg bg-white p-8">
                <Table>
                    <TableHeader>
                        <TableRow location="head">
                            <TableHead>Name</TableHead>
                            <TableHead>Serial number</TableHead>
                            <TableHead className="font-normal">Status</TableHead>
                            <TableHead className="font-normal"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {readers.map((reader) => (
                            <TableRow key={reader.id}>
                                <TableCell>{reader.label || '-'}</TableCell>
                                <TableCell>{reader.serialNumber}</TableCell>
                                <TableCell>
                                    <div
                                        className={
                                            readerStatus(reader) === 'Update required'
                                                ? 'text-danger-400'
                                                : ''
                                        }
                                    >
                                        {readerStatus(reader)}
                                    </div>
                                </TableCell>
                                <TableCell className="w-10">
                                    <Select
                                        align="end"
                                        trigger={
                                            <Button
                                                size="small"
                                                shape="square"
                                                intent="tertiary_deprecated"
                                            >
                                                <Icon
                                                    className="text-gray-500"
                                                    icon="moreHorizontal.medium"
                                                />
                                            </Button>
                                        }
                                        options={[
                                            {
                                                id: reader.requiresRegistration
                                                    ? 'activate'
                                                    : 'edit',
                                                title: reader.requiresRegistration
                                                    ? 'Activate'
                                                    : 'Edit',
                                                icon: reader.requiresRegistration
                                                    ? 'cardReader'
                                                    : 'pencil',
                                            },
                                            {
                                                id: 'delete',
                                                title: 'Remove',
                                                icon: 'trash',
                                                isDanger: true,
                                            },
                                        ]}
                                        onSelectOption={(option) => {
                                            if (option.id === 'edit') editReader(reader)
                                            if (option.id === 'activate') registerReader(reader)
                                            else if (option.id === 'delete')
                                                setReaderToDelete(reader)
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <ConfirmationModal
                isOpen={!!readerToDelete}
                onClose={() => setReaderToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Remove this reader?"
                shouldHideCancelButton
                confirmButtonText="Yes, remove"
            >
                {isProcessing ? (
                    <div className="flex w-full items-center justify-center">
                        <Spinner size={48} />
                    </div>
                ) : (
                    <Paragraph>You can always add it back.</Paragraph>
                )}
            </ConfirmationModal>
        </div>
    )
}
export const ReadersPage = usePSComponent(render, viewWillAppear)
